import { Box, Flex, Grid, GridItem, Image, Input, Text, Drawer, DrawerOverlay, DrawerContent, DrawerCloseButton, DrawerHeader, DrawerBody, FormControl, FormLabel, Button, Select, Center, Spinner, Tooltip, IconButton, } from "@chakra-ui/react";
import MyDiv from "./holdidayMaster.style";
import { AlertCard, CustomButton, CustomToast } from "../../../../components";
import { useEffect, useState } from "react";
import PlusIcon from "../../../../assets/images/Vector.svg";
import { ChevronDownIcon, EditIcon } from "@chakra-ui/icons";
import { IHoliday } from "./model";
import { useFetch } from "../../../../hooks/useFetch";
import CustomTables, { Column, ILazyParams } from "../../../../components/CustomTable/CustomTables";
import { showDate } from "../../../../utilities/helper";

const formatDateForInput = (dateString: string) => {
  if (!dateString) return "";
  const parts = dateString.split("/");
  if (parts.length === 3) {
    const [day, month, year] = parts;
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }
  return dateString;
};

// 🔹 Convert yyyy-MM-dd -> dd/MM/yyyy (for API)
const formatDateForAPI = (dateString: string) => {
  if (!dateString) return "";
  const parts = dateString.split("-");
  if (parts.length === 3) {
    const [year, month, day] = parts;
    return `${day}/${month}/${year}`;
  }
  return dateString;
};

const HolidayMaster = () => {
  const [holidays, setHolidays] = useState<IHoliday[]>([]);
  const [AlertOpen, setAlertOpen] = useState(false);
  const [DrawerOpen, setDrawerOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [pendingStatus, setPendingStatus] = useState<boolean | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [CurrentHolidayMaster, setCurrentHolidayMaster] = useState({ id: 0, holidayName: "", holidayDate: "", status: true, });
  const [statusChangeSource, setStatusChangeSource] = useState<"table" | "drawer" | null>(null);
  const { addToast } = CustomToast();
  const { fetchApi } = useFetch(addToast);
  const [dataLoading, setDataLoading] = useState(false);
  const [lazyParams, setLazyParams] = useState<ILazyParams>({
    first: 0, rows: 0, sortField: undefined, sortOrder: undefined,
    multiSortMeta: [], filters: {}, globalFilter: '',
  });

  useEffect(() => {
    loadHoliday();
  }, [lazyParams]);

  const loadHoliday = async () => {
    setDataLoading(true);
    const params = new URLSearchParams();
    if (lazyParams.globalFilter) params.set('globalFilter', lazyParams.globalFilter);
    if (lazyParams.filters && Object.keys(lazyParams.filters).length) {
      params.set('filters', JSON.stringify(lazyParams.filters));
    }

    const response = await fetchApi<IHoliday[]>(`Hrlense_HolidayMast?${params.toString()}`, "GET");
    if (response) setHolidays(response);
    setDataLoading(false);
  };

  const handleAddNewHolidayMaster = () => {
    setIsEditMode(false);
    setCurrentHolidayMaster({ id: 0, holidayName: "", holidayDate: "", status: true, });
    setDrawerOpen(true);
  };

  const handleEditHoliday = (holiday: IHoliday) => {
    setIsEditMode(true);
    setSelectedId(holiday.id);
    setCurrentHolidayMaster({
      ...holiday,
      holidayDate: formatDateForInput(holiday.holidayDate), // ✅ fixed
    });
    setDrawerOpen(true);
  };

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    setCurrentHolidayMaster(prev => ({
      ...prev,
      [name]: value
    }));
  };


  const handleStatusChange = (id: number, newStatus: boolean) => {
    setSelectedId(id);
    setPendingStatus(newStatus);
    setStatusChangeSource("table");
    setAlertOpen(true);
  };

  const handleDrawerStatusChange = (newStatus: boolean) => {
    setPendingStatus(newStatus);
    setStatusChangeSource("drawer");
    setAlertOpen(true);
  };

  const handleConfirmStatusChange = async () => {
    if (selectedId !== null && pendingStatus !== null) {
      const scoreId = statusChangeSource === "table" ? selectedId : CurrentHolidayMaster.id;

      const response = await fetchApi(`Hrlense_HolidayMast/changeStatus/${scoreId}`, "PUT", { id: scoreId, status: pendingStatus }, null, "Current Holiday updated successfully");
      if (response) {
        if (statusChangeSource === "table") {
          await loadHoliday();
        } else {
          setCurrentHolidayMaster((prev) => ({ ...prev, status: pendingStatus }));
        }
      }
    }
    setAlertOpen(false);
  };

  const handleSubmit = async () => {
    if (!CurrentHolidayMaster.holidayName.trim()) {
      addToast({ message: "Please enter Holiday Name", status: "warning" });
      return;
    }

    if (!CurrentHolidayMaster.holidayDate) {
      addToast({ message: "Please select Holiday Date", status: "warning" });
      return;
    }
    if (isEditMode) {
      const response = await fetchApi(`Hrlense_HolidayMast/${CurrentHolidayMaster.id}`, 'PUT', { id: CurrentHolidayMaster.id, holidayName: CurrentHolidayMaster.holidayName, holidayDate: CurrentHolidayMaster.holidayDate, status: CurrentHolidayMaster.status }, null, "Holiday Master updated successfully");
      if (response) await loadHoliday();
    } else {
      const response = await fetchApi<IHoliday>('Hrlense_HolidayMast', 'POST', { holidayName: CurrentHolidayMaster.holidayName, holidayDate: CurrentHolidayMaster.holidayDate, status: CurrentHolidayMaster.status }, null, "Holiday Mast added successfully");

      if (response) await loadHoliday();
    }
    setDrawerOpen(false);
  };

  const getStatusColor = (status: boolean) => (status ? "#38A169" : "#E53E3E");

  return (
    <MyDiv>
      <Box position="relative" minH="300px">
        <>
          <Flex className="page_heading flex_header">
            <Text className="font-poppins text_semibold text_3xl">Holiday Master</Text>
            <Flex gap={2}>
              <CustomButton title="Add New Holiday" className="btn font-poppins text_sm text_medium" rightIcon={<Image src={PlusIcon} className="logo" />} onClick={handleAddNewHolidayMaster} />
            </Flex>
          </Flex>
          <Grid className="grid_container">
            <GridItem colSpan={12}>
              <CustomTables<IHoliday>
                value={holidays} loading={dataLoading} emptyMessage="No holidays found." preferenceKey="holidayMasterTable" showGlobalSearch={false} showToolbar={true}                
                lazy lazyParams={lazyParams} onLazyParamsChange={setLazyParams} showPagination={false} maxTableHeight={null}
              >
                <Column header="S. No" body={(_, index) => index + 1} />
                <Column header="Holiday Name" field="holidayName" sortable filterable filterType="text" />
                <Column header="Holiday Date" field="holidayDate" sortable filterable filterType="date" body={r => showDate(r.holidayDate)} />
                <Column header="Status" field="status" filterable filterType="boolean" body={(row: any) => (
                    <Select value={row.status ? "true" : "false"} onChange={(e) => handleStatusChange(row.id, e.target.value === "true") }
                      width="120px" height="32px" icon={<ChevronDownIcon />} borderRadius="4px" color={getStatusColor(row.status)} className="font-poppins text_medium text_xsm"
                    >
                      <option value="true" style={{ color: "#38A169" }}> Active</option>
                      <option value="false" style={{ color: "#E53E3E" }}>Inactive</option>
                    </Select>
                  )}
                />
                <Column
                  header="Actions"
                  frozen="right"
                  body={(row: any) => (
                    <Tooltip label="Edit Holiday" hasArrow>
                      <IconButton aria-label="Edit" icon={<EditIcon />} size="sm" colorScheme="orange" variant="ghost" onClick={() => handleEditHoliday(row)} />
                    </Tooltip>
                  )}
                />
              </CustomTables>
            </GridItem>
          </Grid>

          <AlertCard isOpen={AlertOpen} onClose={() => setAlertOpen(false)} onConfirm={handleConfirmStatusChange} />

          <Drawer isOpen={DrawerOpen} placement="right" onClose={() => setDrawerOpen(false)} size="sm">
            <DrawerOverlay />
            <DrawerContent>
              <DrawerCloseButton />
              <DrawerHeader borderBottomWidth="1px">{isEditMode ? "Edit Holiday-Master" : "Add New Holiday-Master"}</DrawerHeader>
              <DrawerBody display="flex" flexDirection="column" justifyContent="space-between" height="100%">
                <Box py={4}>
                  <FormControl mb={4}>
                    <FormLabel>Holiday Name</FormLabel>
                    <Input name="holidayName" value={CurrentHolidayMaster.holidayName} onChange={handleInputChange} placeholder="Hanuman-Jayanti" />
                  </FormControl>
                  <FormControl mb={4}>
                    <FormLabel>Holiday Date</FormLabel>
                    <Input type="date" name="holidayDate" value={CurrentHolidayMaster.holidayDate} onChange={handleInputChange} placeholder="Select Holiday Date" min={new Date().toISOString().split("T")[0]} />
                  </FormControl>
                  <FormControl mb={4}>
                    <FormLabel>Status</FormLabel>
                    <Select w="120px" value={CurrentHolidayMaster.status ? "true" : "false"} onChange={(e) => handleDrawerStatusChange(e.target.value === "true")} color={getStatusColor(CurrentHolidayMaster.status)}>
                      <option value="true" style={{ color: "#38A169" }}> Active</option>
                      <option value="false" style={{ color: "#E53E3E" }}>Inactive</option>
                    </Select>
                  </FormControl>
                </Box>
                <Flex justify="flex-end" mt={4} gap={2}>
                  <Button className="font-poppins text_sm text_medium" onClick={() => setDrawerOpen(false)} style={{ backgroundColor: "#D92F34", color: "white" }} >Cancel</Button>
                  <Button onClick={handleSubmit} className="font-poppins text_sm text_medium" style={{ backgroundColor: "#006838", color: "white" }}>{isEditMode ? "Update Holiday-Master" : "Add New Holiday-Master"}</Button>
                </Flex>
              </DrawerBody>
            </DrawerContent>
          </Drawer>
        </>
      </Box>
    </MyDiv>
  );
};
export default HolidayMaster;